import { useEffect, useState } from "react";
import { FormattedMessage } from "react-intl";
import { useNavigate } from "react-router";
import { Construction } from "@mui/icons-material";
import { encodeBytes32String, getBytes, ZeroAddress } from "ethers";
import { Grid } from "@mui/material";

import { useApiCall } from "@dzambalaorg/react-hooks";
import {
  useAllowance,
  useMetamask,
  useServerSignature,
  useWriteContractWithSimulation,
} from "@dzambalaorg/react-hooks-eth";
import type { IServerSignature } from "@dzambalaorg/types-blockchain";
import {
  convertDatabaseAssetToChainAsset,
  convertDatabaseAssetToTokenTypeAsset,
  formatItem,
} from "@framework/exchange";
import { StyledListWrapper } from "@framework/styled";
import { AddressType, IContract, IDismantle, IToken } from "@framework/types";
import DismantleABI from "@framework/abis/json/ExchangeDismantleFacet/dismantle.json";
import { Card, List, Typography } from "@dzambalaorg/ui-kit";

import { getDismantleMultiplier } from "./utils";

export interface IDismantleTokenPanelProps {
  token: IToken;
}

export const DismantleTokenPanel = (props: IDismantleTokenPanelProps) => {
  const { token } = props;

  const navigate = useNavigate();
  const referrer = ZeroAddress;
  const [rows, setRows] = useState<IDismantle[]>([]);

  const { writeContractAsync } = useWriteContractWithSimulation();

  const { fn: getDismantleFn, isLoading } = useApiCall(
    api =>
      api.fetchJson({
        url: "/recipes/dismantle",
        data: {
          templateId: token.templateId,
        },
      }),
    { success: false, error: false },
  );

  const getDismantle = async () => {
    const json = await getDismantleFn();
    setRows(json.rows);
  };

  const { execute: metaFnWithAllowance } = useAllowance(
    async (values: IDismantle, sign: IServerSignature, systemContract: IContract) => {
      const price = convertDatabaseAssetToChainAsset(values.price!.components);
      const item = convertDatabaseAssetToChainAsset(values.item!.components);
      // set real token Id
      price[0].tokenId = token.tokenId;

      return writeContractAsync({
        address: systemContract.address as AddressType,
        abi: DismantleABI,
        functionName: "dismantle",
        args: [
          {
            externalId: values.id,
            expiresAt: sign.expiresAt,
            nonce: getBytes(sign.nonce),
            extra: encodeBytes32String("0x"),
            receiver: values.merchant!.wallet,
            referrer: ZeroAddress,
          },
          // ITEM to get after dismantle
          item,
          // PRICE token to dismantle
          price[0],
          sign.signature,
        ],
      });
    },
  );

  const metaFnWithSign = useServerSignature((values: IDismantle, sign: IServerSignature, systemContract: IContract) => {
    const assets = convertDatabaseAssetToTokenTypeAsset(values.price!.components);
    return metaFnWithAllowance({ contract: systemContract.address, assets }, values, sign, systemContract);
  });

  const { execute: metaFn } = useMetamask((values: IDismantle) => {
    return metaFnWithSign(
      {
        url: "/recipes/dismantle/sign",
        method: "POST",
        data: {
          referrer,
          dismantleId: values.id,
          tokenId: token.id,
        },
      },
      values,
    ) as Promise<void>;
  });

  const handleDismantle = (dismantle: IDismantle) => {
    return async () =>
      await metaFn(dismantle).then(() => {
        void navigate("/tokens");
      });
  };

  useEffect(() => {
    if (!rows.length) {
      void getDismantle();
    }
  }, []);

  if (isLoading) {
    return null;
  }

  if (!rows.length) {
    return null;
  }

  return (
    <Card>
      <Card.Content>
        <Typography variant="h5" component="p">
          <FormattedMessage id="pages.token.dismantle" />
        </Typography>
        <StyledListWrapper count={rows.length} isLoading={isLoading}>
          {rows.map(dismantle => {
            const { multiplier } = getDismantleMultiplier(
              "1",
              token.metadata,
              dismantle.dismantleStrategy,
              dismantle.rarityMultiplier,
            );
            return (
              <List.ItemButton key={dismantle.id} onClick={handleDismantle(dismantle)}>
                <List.ItemIcon>
                  <Construction />
                </List.ItemIcon>
                <List.ItemText>
                  <Grid container spacing={1} alignItems="flex-center">
                    <Grid size={{ xs: 12 }}>{formatItem(dismantle.item)}</Grid>
                    <Grid size={{ xs: 12 }}>
                      {multiplier !== 1 ? (
                        <FormattedMessage id="pages.token.rarityMultiplier" values={{ multiplier }} />
                      ) : null}
                    </Grid>
                  </Grid>
                </List.ItemText>
              </List.ItemButton>
            );
          })}
        </StyledListWrapper>
      </Card.Content>
    </Card>
  );
};
